import { Component, ElementRef, inject, ViewChild } from '@angular/core';
import { DialogComponent, NamedDialog } from '@tytapp/common';
import { ContentBlockCategory, ContentBlocksService } from '../content-blocks.service';
import { ContentBlockType } from '../content-block-type';
import { isClientSide } from '@tytapp/environment-utils';
import { ContentBlock } from '@tytapp/content';

@NamedDialog('content-block-picker')
@Component({
    templateUrl: './content-block-picker.component.html',
    styleUrls: ['./content-block-picker.component.scss'],
    standalone: false
})
export class ContentBlockPickerComponent extends DialogComponent {
    callback: (block: ContentBlockType<ContentBlock> | { blocks: ContentBlockType[]; category: ContentBlockCategory }) => void;

    init(callback: (block: ContentBlockType) => void) {
        if (!callback)
            alert(`No callback provided to block picker!`);

        this.callback = callback;

        if (isClientSide()) {
            document.addEventListener('keydown', this.keyHandler);
        }
    }

    destroy() {
        if (isClientSide()) {
            document.removeEventListener('keydown', this.keyHandler);
        }
    }

    private keyHandler = (ev: KeyboardEvent) => {
        //console.log(ev.key);
        if (ev.key === 'ArrowLeft')
            this._selected = Math.max(0, this.selected - 1);
        else if (ev.key === 'ArrowRight')
            this._selected = Math.min(this.allBlockTypes.length - 1, this.selected + 1);
        else if (ev.key === 'Enter') {
            let type = this.allBlockTypes[this.selected];
            if (type)
                this.insert(type);
        }
    }

    @ViewChild('searchInput') searchInput: ElementRef<HTMLInputElement>;

    ngAfterViewInit() {
        if (isClientSide()) {
            setTimeout(() => {
                this.searchInput.nativeElement.focus();
            });
        }
    }

    query: string;
    blockTypes = inject(ContentBlocksService);
    _selected = 0;
    showCategories: boolean = true;
    showPreviews: boolean = false;

    get selected() {
        return Math.max(0, Math.min(this.allBlockTypes.length - 1, this._selected));
    }

    get allBlockTypes() {
        return this.blockTypes.all().filter(x => JSON.stringify(x).toLowerCase().includes(this.query?.toLowerCase() ?? ''));
    }

    get categorizedBlockTypes() {
        const categories = this.blockTypes.getCategories();
        const categorizedBlocks = categories.map(category => ({
            category,
            blocks: this.allBlockTypes.filter(block => block.categoryId === category.id)
        })).filter(x => x.blocks.length > 0);

        const uncategorizedBlocks = this.allBlockTypes.filter(block => !block.categoryId);

        return { categorizedBlocks, uncategorizedBlocks };
    }

    previewOf(blockType: ContentBlockType) {
        return this.blockTypes.getPreview(blockType);
    }

    insert(block: ContentBlockType) {
        this.callback(block);
        this.close();
    }

    trackByCategory(index: number, category: any): any {
        return category.category.id; // Assuming each category has a unique 'id' property
    }
}
